import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import SelectableComponent from '../interfaces/selectableComponent';
import { IssuecardComponent } from '../components/issuecard/issuecard.component';
import { SprintcardComponent } from '../components/sprintcard/sprintcard.component';

@Injectable({
  providedIn: 'root',
  })
export class DynamicElementService {

  dynamicElementInsertionPoint: ViewContainerRef;
  private componentRefs = [];
  private readonly baseZIndex = 25;

  constructor() { }

  public setInsertionPoint(insertionPoint: ViewContainerRef): void {
    this.dynamicElementInsertionPoint = insertionPoint;
  }

  public select(componentRef: ComponentRef<any>): void {
    if (this.contains(componentRef) && componentRef !== this.componentRefs[this.componentRefs.length - 1]) {
      const i = this.componentRefs.indexOf(componentRef);
      this.componentRefs.splice(i, 1);
      this.componentRefs.push(componentRef);
      this.reorder();
    }
  }

  public insert(component: ComponentRef<any>): void {
    if (!this.contains(component)) {
      this.componentRefs.push(component);
      this.dynamicElementInsertionPoint.insert(component.hostView);
      this.reorder();
    }
  }

  public remove(componentRef: ComponentRef<any>): void {
    if (this.contains(componentRef)) {
      const i = this.componentRefs.indexOf(componentRef);
      this.componentRefs.splice(i, 1);

      const index = this.dynamicElementInsertionPoint.indexOf(componentRef.hostView);
      if (index >= 0) {
        this.dynamicElementInsertionPoint.detach(index);
      }

      (<SelectableComponent>componentRef.instance).zIndex = this.baseZIndex;
      this.reorder();
    }
  }

  public destroyAll(componentRefs: ComponentRef<any>[]): void {
    componentRefs
      .filter(componentRef => this.contains(componentRef))
      .forEach(componentRef => {
        const i = this.componentRefs.indexOf(componentRef);
        this.componentRefs.splice(i, 1);

        const index = this.dynamicElementInsertionPoint.indexOf(componentRef.hostView);
        if (index >= 0) {
          this.dynamicElementInsertionPoint.remove(index);
        }
      });

    this.reorder();
  }

  public destroy(componentRef: ComponentRef<any>): void {
    if (this.contains(componentRef)) {
      const i = this.componentRefs.indexOf(componentRef);
      this.componentRefs.splice(i, 1);

      const index = this.dynamicElementInsertionPoint.indexOf(componentRef.hostView);
      if (index >= 0) {
        this.dynamicElementInsertionPoint.remove(index);
      }

      this.reorder();
    }
  }

  private reorder(): void {
    this.componentRefs.forEach((ref, index) => {
      const componentRef: ComponentRef<any> = <ComponentRef<any>>this.componentRefs[index];
      (<SelectableComponent>componentRef.instance).zIndex = this.baseZIndex + Number(index);
    });
  }

  public clearInsertionPoint(): void {
    this.componentRefs = [];
    this.dynamicElementInsertionPoint.clear();
  }

  /**
   * Returns the componentRef for the WidgetWrapper containing the widget with 'widgetName' or
   * null if no such component is in the dynamic container.
   *
   * Notice:
   * We cant hardtype the ComponentRef (as ComponentRef<WidgetWrapperComponent) as this would lead
   * to a circular reference.
   */
  public getWrapperComponentRefForWidget(widgetName: string): ComponentRef<any> {
    let result: ComponentRef<any> = null;
    this.componentRefs.forEach(ref => {
      // If the instance of this component has the properyt 'widgetName' we assume its a widgetwrapper-Ref
      if (ref.instance.widgetName && ref.instance.widgetName === widgetName) {
        result = ref;
      }
    });
    return result;
  }

  public getInsertionPoint(): ViewContainerRef {
    return this.dynamicElementInsertionPoint;
  }

  private contains(componentRef: ComponentRef<any>) {
    return this.componentRefs.indexOf(componentRef) !== -1;
  }

  public containsIssuecardForIssue(issueId: number): boolean {
    return (this.getIssuecardForIssue(issueId) != null);
  }

  public getIssuecardForIssue(issueId: number): IssuecardComponent {
    const ref: ComponentRef<any> = this.getComponentRefForIssue(issueId);
    if (ref && (ref.instance instanceof IssuecardComponent)) {
      return ref.instance;
    }
    else { return null; }
  }

  public getSprintcard(sprintId: number): SprintcardComponent {
    const ref = this.getComponentRefForSprint(sprintId);
    return (ref && (ref.instance instanceof SprintcardComponent)) ? ref.instance : null;
  }

  public getComponentRefForIssue(issueId: number): ComponentRef<any> {
    return this.componentRefs.find(ref => Number(ref.instance.issue?.id) === Number(issueId));
  }
  public getAllIssueCards(): IssuecardComponent[] {
    const issueCards: IssuecardComponent[]= [];
    this.componentRefs.forEach(component => {
      if (component.instance instanceof IssuecardComponent){
        issueCards.push(component.instance);
      }
    });
    return issueCards;
  }

  public getComponentRefForSprint(sprintId: number): ComponentRef<any> {
    return this.componentRefs.find(ref => Number(ref.instance.sprint?.id) === Number(sprintId));
  }

}
